import { ApiError, NetworkError } from '@/api/client';
import { useT, type TranslateFn } from '@/i18n/LanguageContext';

/**
 * Renders any thrown error as: a clear message, a concrete recovery action,
 * and the visible request_id a real backend error carries (ADR-058 §5) -
 * never a raw stack trace, never a silently swallowed failure.
 */
export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }): JSX.Element {
  const t = useT();
  if (error instanceof ApiError) {
    return (
      <div className="error-banner" role="alert">
        <div>
          <span className="code">{error.code}</span> — {describeCode(t, error.code, error.status)}
        </div>
        {onRetry && (
          <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
            {t('common.retry')}
          </button>
        )}
        <div className="rid">request_id: {error.requestId}</div>
      </div>
    );
  }
  if (error instanceof NetworkError) {
    return (
      <div className="error-banner" role="alert">
        <div>{error.message}</div>
        {onRetry && (
          <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="error-banner" role="alert">
      <div>{t('error.unexpected')}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

function describeCode(t: TranslateFn, code: string, status: number): string {
  if (code === 'ROLE.LAST_ADMIN') return t('error.roleLastAdmin');
  if (code === 'ROLE.UNKNOWN') return t('error.roleUnknown');
  if (status === 403) return t('error.forbidden');
  if (status === 404) return t('error.notFound');
  if (status === 401) return t('error.sessionExpired');
  if (status === 429) return t('error.tooManyRequests');
  if (code === 'INTERNAL.UNEXPECTED') return t('error.serverUnexpected');
  return t('error.requestFailed');
}
